import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ArrowRight, Package, Users, HelpCircle, Phone, Building } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface NavItem {
  key: string;
  href: string;
  label: string;
}

interface NavMoreMenuProps {
  items: NavItem[];
  className?: string;
  navDark?: boolean;
}

const getIconMeta = (key: string): { icon: typeof Package; color: string; bg: string } => {
  switch (key) {
    case 'how_it_works': return { icon: Package, color: 'text-sky-400', bg: 'bg-sky-400/15' };
    case 'for_couriers': return { icon: Users, color: 'text-emerald-400', bg: 'bg-emerald-400/15' };
    case 'faq': return { icon: HelpCircle, color: 'text-amber-400', bg: 'bg-amber-400/15' };
    case 'support': return { icon: Phone, color: 'text-violet-400', bg: 'bg-violet-400/15' };
    case 'about': return { icon: Building, color: 'text-rose-400', bg: 'bg-rose-400/15' };
    default: return { icon: ArrowRight, color: 'text-white/50', bg: 'bg-white/[0.06]' };
  }
};

const NavMoreMenu = ({ items, className, navDark = false }: NavMoreMenuProps) => {
  const { t } = useTranslation();
  const { localePath } = useLocalizedPath();
  const location = useLocation();

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        className={cn(
          "px-3 py-2 text-sm font-semibold transition-colors duration-[var(--duration-fast)] ease-[var(--ease)] whitespace-nowrap flex items-center gap-1 border-0 outline-none ring-0 focus:ring-0 focus:outline-none",
          "text-white/70 hover:text-white",
          className
        )}
      >
        {t('nav.menu')}
        <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200" />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="bottom"
        align="end"
        sideOffset={8}
        className="w-64 p-0 shadow-2xl border-white/10 rounded-none"
        style={{ background: 'var(--color-navy)', zIndex: 'var(--z-dropdown, 50)' }}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-white/10 bg-white/[0.04]">
          <p className="text-[10px] font-semibold text-white/30 uppercase tracking-wider">
            {t('chrome.quickNavigation')}
          </p>
        </div>

        {/* Items */}
        <div className="py-1">
          {items.map((item) => {
            const { icon: Icon, color, bg } = getIconMeta(item.key);
            const localizedHref = localePath(item.href);
            const isActive = location.pathname === localizedHref;

            return (
              <DropdownMenuItem
                key={item.key}
                asChild
                className="px-4 py-2.5 cursor-pointer focus:bg-white/[0.06] hover:bg-white/[0.06] transition-colors"
              >
                <Link to={localizedHref} className="flex items-center gap-3 w-full">
                  <div
                    className={cn(
                      "w-7 h-7 flex items-center justify-center transition-colors",
                      isActive
                        ? "border-2 border-[var(--color-orange)] bg-transparent text-[var(--color-orange)] font-semibold hover:bg-[var(--color-navy)]"
                        : `${bg} ${color}`
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <span
                    className={cn(
                      "text-sm font-medium",
                      isActive ? "text-[var(--color-orange)]" : "text-white/70"
                    )}
                  >
                    {item.label}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-white/20 ml-auto" />
                </Link>
              </DropdownMenuItem>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-white/10 bg-white/[0.02]">
          <Link
            to={localePath("/support")}
            className="text-xs text-[var(--color-orange)] hover:text-[var(--color-orange-light)] font-medium transition-colors"
          >
            {t('chrome.needHelp')}
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NavMoreMenu;
